import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import style from './TemplateFunctionsContainer.module.css';
import ExportModal from './exportModal/ExportModal';
import TextButton from '../../../atomic/buttons/textButton/TextButton';
import ImportModal from './importModal/ImportModal';
import SetDefaultValuesBasedOnStoredTpConfig from '../AddTradePlan/SetDefaultValuesBasedOnStoredConfig.js';
// import SavedTradePlanConfigsObservable from '../../../../znotusedcurrently/rxjs/observables/SavedTradePlanConfigsObservable.js2';
import DataList from '../../../atomic/inputs/datalist/DataList';
import OverwriteModal from './overwriteModal/OverwriteModal';
import { colors } from '../../../../colorVars';
import DeleteModal from './deleteModal/DeleteModal';
import { addSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/addSavedTradePlanConfig';
import { updateSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/updateSavedTradePlanConfig';
import { removeSavedTradePlanConfig } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/savetradeplanconfig/removeSavedTradePlanConfig';
import { retrieveUserTemplates } from '../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/importexporttradeplan/retrieveUserTemplates';
import { useWebsocketStore } from '../../../../stores/websocketStore/websocketProvider';
import { observer } from 'mobx-react-lite';
import {
  TEMPLATE_TYPES,
  addTemplateTypeToPayload,
  resolveTemplateSaveBehavior,
} from './templateTypeUtils';

const TemplateFunctionsContainer = observer(({
  data,
  packageOfSetters,
  mutableSymbol,
  focusRef,
  setClearForm,
  setRefreshedForm,
  setConfigTitle,
  setSelectedSavedConfig,
  configTitle,
}) => {
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState('');
  const [dataListVal, setDataListVal] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const { savedTradePlanTemplatesStore } = useWebsocketStore();
  const templates = savedTradePlanTemplatesStore.savedTemplates;
  const templatesList = savedTradePlanTemplatesStore.templatesList;

  const checkIfTemplateExists = (val) => {
    if (templates[val]) return true;
    else return false;
  };
  const findTemplate = () => {
    return templates[dataListVal];
  };

  useEffect(() => {
    setDataListVal(configTitle ?? '');
  }, []);

  // closure function that returns SetDefaultValues function with required symbol and setters
  const loadIntoTradePlan = (template) => {
    if (template !== undefined) {
      setConfigTitle(dataListVal);
      setSelectedSavedConfig(true);
      return SetDefaultValuesBasedOnStoredTpConfig(
        packageOfSetters,
        template,
        mutableSymbol
      );
    }
  };

  const loadFromSavedConfigs = () => {
    // check if currently selected name is a saved config
    const exists = findTemplate();
    //if it does, load it into the trade plan
    if (exists) {
      loadIntoTradePlan(exists.tradePlanJson);
    }
  };

  const saveTradePlan = () => {
    //check if template title is already used, if it is, open overwrite modal
    const saveBehavior = resolveTemplateSaveBehavior({
      requestedName: dataListVal,
      templateType: TEMPLATE_TYPES.STANDARD,
      existingTemplates: templates,
    });
    if (saveBehavior.shouldPromptOverwrite) {
      setOverwriteOpen(true);
      return;
    }
    //check if title field is empty, if it is, show an error
    else if (dataListVal === '') {
      setMessage('Please give your template a name.');
      setMessageType('error');
      return;
    }
    // if neither is true, save trade plan to DB
    else {
      addSavedTradePlanConfig(
        saveBehavior.saveName,
        addTemplateTypeToPayload(data, TEMPLATE_TYPES.STANDARD),
        TEMPLATE_TYPES.STANDARD
      );
      setDataListVal(saveBehavior.saveName);
      setMessage(
        saveBehavior.usedSaveAsNew
          ? `Template saved as ${saveBehavior.saveName}.`
          : 'Trade plan saved!'
      );
      setMessageType('success');
      setConfigTitle(saveBehavior.saveName);
    }
  };
  const openDeleteModal = () => {
    const result = findTemplate();
    if (result) {
      setDeleteOpen(true);
      setDeleteResult(result);
    } else {
      setMessageType('error');
      setMessage('Template does not exist');
    }
  };
  const deleteTradePlan = () => {
    const result = findTemplate();
    if (result) {
      removeSavedTradePlanConfig({
        key: result.key,
        configName: result.configurationName,
      });
      setMessageType('success');
      setMessage('Template deleted!');
      setDataListVal('');
      setConfigTitle('');
      setSelectedSavedConfig(false);
    }
  };

  return (
    <div className={style.outerContainer}>
      <hr />
      <h6>Template Utilities</h6>
      <DataList
        dataList={templatesList}
        dataMapFunc={(obj) => (
          <option key={obj.key} value={obj.configurationName} />
        )}
        inputVal={dataListVal}
        inputHandler={(value) => {
          setDataListVal(value);
          setMessage('');
        }}
        id="template"
        ref={focusRef}
        onBlur={() => setConfigTitle(dataListVal)}
      >
        Template Name
      </DataList>
      <p className={messageType === 'error' ? style.error : style.success}>
        {message}
      </p>

      <div className={style.buttonContainer}>
        <ImportModal
          open={importOpen}
          handleClose={() => setImportOpen(false)}
          handleLoad={loadIntoTradePlan}
        />
        <ExportModal
          open={exportOpen}
          handleClose={() => setExportOpen(false)}
          data={data}
        />
        <OverwriteModal
          configName={dataListVal}
          open={overwriteOpen}
          handleClose={() => setOverwriteOpen(false)}
          updateConfig={() => {
            const key = findTemplate().key;
            setConfigTitle(dataListVal);
            updateSavedTradePlanConfig({
              tradePlanObject: addTemplateTypeToPayload(
                data,
                TEMPLATE_TYPES.STANDARD
              ),
              key,
              configName: dataListVal,
              templateType: TEMPLATE_TYPES.STANDARD,
            });
            setOverwriteOpen(false);

            setMessage('Trade plan saved!');
            setMessageType('success');
          }}
        />
        <DeleteModal
          result={deleteResult}
          deleteConfig={deleteTradePlan}
          open={deleteOpen}
          handleClose={() => setDeleteOpen(false)}
        />
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.blue}
          clickHandler={saveTradePlan}
        >
          Save
        </TextButton>
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.blue}
          clickHandler={loadFromSavedConfigs}
        >
          Load
        </TextButton>
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.blue}
          clickHandler={openDeleteModal}
        >
          Delete
        </TextButton>
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.blue}
          clickHandler={() => {
            setRefreshedForm(true);
            setClearForm(true);
          }}
        >
          Clear
        </TextButton>
        <div
          style={{
            borderRight: '1px solid rgba(0, 0, 0, 0.2)',
            margin: '0 4px',
          }}
        ></div>
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.green}
          clickHandler={() => {
            setConfigTitle('');
            setSelectedSavedConfig(false);
            setExportOpen(true);
            retrieveUserTemplates();
          }}
        >
          Share
        </TextButton>
        <TextButton
          fontSize="medium"
          color="white"
          backgroundColor={colors.green}
          clickHandler={() => setImportOpen(true)}
        >
          Import
        </TextButton>
      </div>
      <hr />
    </div>
  );
});


TemplateFunctionsContainer.propTypes = {
  data: PropTypes.object.isRequired,
  packageOfSetters: PropTypes.array.isRequired,
  mutableSymbol: PropTypes.object,
  savedConfigurations: PropTypes.object.isRequired,
  focusRef: PropTypes.object,
  setClearForm: PropTypes.func.isRequired,
  setRefreshedForm: PropTypes.func.isRequired,
  setConfigTitle: PropTypes.func,
  setSelectedSavedConfig: PropTypes.func,
  configTitle: PropTypes.string,
};

export default TemplateFunctionsContainer;
